import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, exhaustMap, map, mergeMap, of, switchMap } from 'rxjs';

import { ProjectContactService } from '../../services/project-contact.service';
import { ProjectContactActions } from '../state/project-contact/project-contact.actions';

export const loadProjectContacts = createEffect(
  (actions$ = inject(Actions), projectContactService = inject(ProjectContactService)) => {
    return actions$.pipe(
      ofType(ProjectContactActions.loadContacts),
      switchMap(({ projectId }) => {
        return projectContactService.findAll(projectId).pipe(
          map((contacts) =>
            ProjectContactActions.loadContactsSuccess({ projectId, contacts }),
          ),
          catchError(() =>
            of(ProjectContactActions.loadContactsFail({ projectId })),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const createProjectContact = createEffect(
  (actions$ = inject(Actions), projectContactService = inject(ProjectContactService)) => {
    return actions$.pipe(
      ofType(ProjectContactActions.createContact),
      concatMap(({ projectId, contact }) => {
        return projectContactService.create(projectId, contact).pipe(
          map((contact) =>
            ProjectContactActions.createContactSuccess({ contact }),
          ),
          catchError(() => of(ProjectContactActions.createContactFail())),
        );
      }),
    );
  },
  { functional: true },
);

export const createProjectContacts = createEffect(
  (actions$ = inject(Actions), projectContactService = inject(ProjectContactService)) => {
    return actions$.pipe(
      ofType(ProjectContactActions.createContacts),
      concatMap(({ projectId, contacts }) => {
        return projectContactService.bulkCreate(projectId, contacts).pipe(
          map((contacts) =>
            ProjectContactActions.createContactsSuccess({ contacts }),
          ),
          catchError(() => of(ProjectContactActions.createContactsFail())),
        );
      }),
    );
  },
  { functional: true },
);

export const updateProjectContact = createEffect(
  (actions$ = inject(Actions), projectContactService = inject(ProjectContactService)) => {
    return actions$.pipe(
      ofType(ProjectContactActions.updateContact),
      exhaustMap(({ contactId, payload }) => {
        return projectContactService.update(contactId, payload).pipe(
          map((contact) =>
            ProjectContactActions.updateContactSuccess({ contact }),
          ),
          catchError(() =>
            of(ProjectContactActions.updateContactFail({ contactId })),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const deleteProjectContact = createEffect(
  (actions$ = inject(Actions), projectContactService = inject(ProjectContactService)) => {
    return actions$.pipe(
      ofType(ProjectContactActions.deleteContact),
      mergeMap(({ contactId }) => {
        return projectContactService.remove(contactId).pipe(
          map(() => ProjectContactActions.deleteContactSuccess({ contactId })),
          catchError(() =>
            of(ProjectContactActions.deleteContactFail({ contactId })),
          ),
        );
      }),
    );
  },
  { functional: true },
);
